// Acil iş (migration 0069): the red bar every floor screen shows while something must be done now about
// an order (its carrier changed after the label printed, it was cancelled with goods out of their bins,
// …), its panel (#/acil) and the sound a new one makes. The rules the screens follow, kept apart from
// React so they are tested on their own.
import { normalizeScan, scanSpellings, URGENT_DONE_HOW_LABELS, type UrgentAction, type UrgentDetails } from '@wms/domain';

/** This device does not sound new acil işler ("Sesi kapat"); kept per device in localStorage. */
export const URGENT_MUTE_KEY = 'wms.urgent.muted';

/** The slice of localStorage the mute switch needs (session.ts store: it never throws). */
export interface KeyStore {
  get(key: string): string | null;
  set(key: string, value: string | null): void;
}

export const urgentMuted = (store: KeyStore): boolean => store.get(URGENT_MUTE_KEY) === '1';
export const setUrgentMuted = (store: KeyStore, muted: boolean): void => store.set(URGENT_MUTE_KEY, muted ? '1' : null);

/**
 * The acil işler that arrived since the last look, each once. The first look (`seen` null, the screen
 * just opened) hears none: those were there before; later looks hear the ids not seen before. `seen`
 * keeps every id seen, so one that went and came back (a newer event on it) is not heard twice.
 */
export function arrivals(seen: ReadonlySet<string> | null, actions: readonly Pick<UrgentAction, 'id'>[]): { readonly seen: Set<string>; readonly fresh: string[] } {
  const next = new Set(seen ?? []);
  const fresh: string[] = [];
  for (const a of actions) {
    if (next.has(a.id)) continue;
    next.add(a.id);
    if (seen !== null) fresh.push(a.id);
  }
  return { seen: next, fresh };
}

/**
 * What a look that found `fresh` new acil işler does on this device: the siren unless the device was muted, and
 * a buzz either way (the switch is the sound's: a phone in a pocket on a quiet floor still feels a new one).
 */
export function alarmOf(fresh: number, muted: boolean): { readonly siren: boolean; readonly buzz: boolean } {
  return { siren: fresh > 0 && !muted, buzz: fresh > 0 };
}

/** How an acil iş is closed on its panel: a scan of the new label, Rafa iade, "Etiketi attım", a desk packing it again. */
export type UrgentControl = 'scan' | 'putaway' | 'discard' | 'desk';

export function urgentControl(action: Pick<UrgentAction, 'kind'>): UrgentControl {
  switch (action.kind) {
    case 'relabel':
      return 'scan';
    case 'put_back':
      return 'putaway';
    case 'discard_label':
      return 'discard';
    case 'repack':
      return 'desk';
  }
}

/** Under the instruction: how this acil iş goes away. */
export function urgentHint(action: Pick<UrgentAction, 'kind' | 'orderStatus'>): string {
  switch (action.kind) {
    case 'relabel':
      // an order not packed yet: the desk takes the old label off before it packs, so no scan closes it here
      return action.orderStatus === 'packed'
        ? 'Yeni etiketi yapıştırınca yukarıdaki alana ya da Sevkiyat’ta okutun: iş kapanır. Eski etiket okutulursa reddedilir.'
        : 'Sipariş paketleme masasında yeni etiketle paketlenince kendiliğinden kapanır.';
    case 'put_back':
      return 'Yerleştirme → Rafa iade görevleri bitince kendiliğinden kapanır.';
    case 'discard_label':
      return 'Etiketi attıktan sonra “Etiketi attım”a basın.';
    case 'repack':
      return 'Paket masada yeniden paketlenince kendiliğinden kapanır.';
  }
}

/**
 * The open relabel a code read on the panel belongs to: its new label, a label printed before the carrier
 * change (the server refuses that one and says why) or its order number. The relabel of an order not packed
 * yet is found too: the server says it closes at the desk that packs it. A reader on another keyboard
 * layout is read as it meant the code. Null: no open relabel has it.
 */
export function relabelFor(code: string, actions: readonly UrgentAction[]): UrgentAction | null {
  const read = scanSpellings(code);
  const is = (value: string | null | undefined) => typeof value === 'string' && value !== '' && read.includes(normalizeScan(value));
  return (
    actions.find(
      (a) =>
        a.kind === 'relabel' &&
        a.doneAt === null &&
        (is(a.trackingNumber) || is(a.orderNumber) || a.supersededTracking.some(is) || is(a.details.newTracking) || is(a.details.oldTracking)),
    ) ?? null
  );
}

/** The carrier stayed and only the tracking number changed (Hepsiburada packaged the lines again): not "kargo değişti". */
const sameCarrier = (d: Pick<UrgentDetails, 'from' | 'to'>) => typeof d.to === 'string' && d.from === d.to;

/** The chip of an order with an open relabel, on the desk's queue and at Sevkiyat. */
export const relabelChip = (d: Pick<UrgentDetails, 'from' | 'to'>): string => (sameCarrier(d) ? 'Takip no değişti' : 'Kargo değişti');

/**
 * The desk's red banner of a relabel: its headline, and, before the order is packed, what the packer does (a
 * parcel packed already reads the relabel's whole instruction).
 */
export function relabelBanner(d: Pick<UrgentDetails, 'from' | 'to'>): { readonly headline: string; readonly before: string } {
  const what = 'eski etiketi sökün (ürüne yapıştırıldıysa), yeni etiket bu masadan basılır.';
  return sameCarrier(d)
    ? { headline: 'TAKİP NO DEĞİŞTİ', before: `${d.to} takip numarası değişti: ${what}` }
    : { headline: 'KARGO DEĞİŞTİ', before: `${d.from ?? 'Eski firma'} → ${d.to ?? 'yeni firma'}: ${what}` };
}

/**
 * Sevkiyat's KARGOYA VERMEYİN (after the order number) for a parcel whose carrier changed after its label
 * printed: the old label was read (LABEL_SUPERSEDED), or anything but the new one (RELABEL_OPEN).
 */
export function relabelStopText(code: string, trackingNumber: string | null | undefined): string {
  const fresh = trackingNumber ? `yeni etiketi (takip no ${trackingNumber})` : 'yeni etiketi';
  return code === 'LABEL_SUPERSEDED'
    ? `Bu etiket geçersiz: kargo firması değişti. Eski etiketi sökün, ${fresh} yapıştırıp okutun.`
    : `Kargo değişti: paketin üstündeki eski etiketi sökün, ${fresh} yapıştırıp okutun.`;
}

/** "Ayşe Kaya · yeni etiket okutuldu", "iş yapılınca kendiliğinden": who closed it and how. */
export function doneText(action: Pick<UrgentAction, 'doneBy' | 'doneHow'>): string {
  const how = action.doneHow ? URGENT_DONE_HOW_LABELS[action.doneHow] : '';
  return [action.doneBy, how].filter(Boolean).join(' · ');
}
